import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { aoReconectar, manterConectada, notificationHub, stopNotificationHub } from '@/lib/realtime';
import type { Notificacao } from '@/lib/notifications';
import { toast } from '@/lib/toast';

/**
 * Notificações em tempo real enquanto houver sessão. Montado uma vez, no AppLayout.
 *
 * Ao receber `NovaNotificacao` o hook só invalida a query — não mantém lista própria, que seria
 * uma segunda fonte de verdade para o mesmo dado.
 *
 * Depende do id do usuário: no logout (ou troca de usuário) a conexão é descartada, porque o
 * grupo do hub é por usuário e a sessão seguinte precisa conectar com o próprio token.
 */
export function useNotificationHub(usuarioId: string | undefined) {
  const qc = useQueryClient();

  useEffect(() => {
    if (!usuarioId) return;

    const conn = notificationHub();
    const revalidar = () => qc.invalidateQueries({ queryKey: ['notifications'] });
    const onNova = (n: Notificacao) => {
      revalidar();
      toast.info(n.titulo);
    };

    conn.on('NovaNotificacao', onNova);
    // Uma reconexão pode ter perdido eventos no intervalo: revalidar cobre a lacuna.
    const cancelarReconexao = aoReconectar(conn, revalidar);
    // Se a API estiver fora do ar no carregamento, continua tentando. Enquanto isso o sino segue
    // pelo polling de fallback (useNotifications) e o ConexaoBanner avisa.
    const cancelarConexao = manterConectada(conn);

    return () => {
      conn.off('NovaNotificacao', onNova);
      cancelarReconexao();
      cancelarConexao();
      void stopNotificationHub();
    };
  }, [usuarioId, qc]);
}
